import React from 'react';
import { ArrowLeft, ArrowRight, CircleHelp, LucideIcon, Settings2 } from 'lucide-react';

export interface ModuleExperienceMetric {
  label: string;
  value: string | number;
  helper: string;
}

export interface ModuleExperienceTask {
  title: string;
  description: string;
  icon: LucideIcon;
  onClick?: () => void;
}

interface ModuleExperienceHomeProps {
  eyebrow: string;
  title: string;
  description: string;
  explanation: string;
  icon: LucideIcon;
  metrics: ModuleExperienceMetric[];
  tasks: ModuleExperienceTask[];
  primaryActionLabel: string;
  onOpenOperations: () => void;
}

interface ModuleOperationsFrameProps {
  backLabel: string;
  onBack: () => void;
  contextLabel?: string;
  children: React.ReactNode;
}

export const ModuleOperationsFrame: React.FC<ModuleOperationsFrameProps> = ({ backLabel, onBack, contextLabel = 'Vista completa', children }) => (
  <div className="flex h-full min-h-0 flex-col bg-slate-50">
    <div className="flex h-14 shrink-0 items-center justify-between gap-3 border-b border-slate-200 bg-white px-3 sm:px-5">
      <button type="button" onClick={onBack} className="flex min-h-10 items-center gap-2 rounded-xl px-3 text-sm font-black text-slate-700 transition-colors hover:bg-slate-100">
        <ArrowLeft size={17} /> {backLabel}
      </button>
      <span className="hidden text-xs font-bold text-slate-400 sm:block">{contextLabel}</span>
    </div>
    <div className="min-h-0 flex-1">{children}</div>
  </div>
);

export const ModuleExperienceHome: React.FC<ModuleExperienceHomeProps> = ({
  eyebrow,
  title,
  description,
  explanation,
  icon: Icon,
  metrics,
  tasks,
  primaryActionLabel,
  onOpenOperations,
}) => (
  <div className="h-full overflow-y-auto bg-[#f8faf9] pb-24 md:pb-10" data-screen-label={`${title} - inicio simple`}>
    <div className="mx-auto max-w-6xl px-4 py-7 sm:px-8 sm:py-10">
      <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 pl-12 sm:pl-0">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald-600 text-white shadow-sm">
              <Icon size={21} />
            </span>
            <p className="text-xs font-black uppercase tracking-[0.14em] text-emerald-700">{eyebrow}</p>
          </div>
          <h1 className="mt-5 text-3xl font-black leading-[1.05] tracking-[-0.04em] text-slate-950 sm:text-5xl">{title}</h1>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600 sm:text-base">{description}</p>
        </div>
        <button type="button" onClick={onOpenOperations} className="flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-slate-950 px-5 text-sm font-black text-white transition-colors hover:bg-slate-800">
          {primaryActionLabel} <ArrowRight size={16} />
        </button>
      </div>

      <div className="mt-8 flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-slate-800 sm:p-5">
        <CircleHelp size={19} className="mt-0.5 shrink-0 text-slate-500" />
        <div>
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">En pocas palabras</p>
          <p className="mt-1 text-sm font-semibold leading-6">{explanation}</p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {metrics.map((metric) => (
          <div key={metric.label} className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
            <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">{metric.label}</p>
            <p className="mt-3 break-words text-2xl font-black text-slate-950 sm:text-3xl">{metric.value}</p>
            <p className="mt-2 text-xs leading-5 text-slate-500">{metric.helper}</p>
          </div>
        ))}
      </div>

      <div className="mt-8">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-black uppercase tracking-widest text-slate-500">Acciones</p>
            <h2 className="mt-1 text-xl font-black text-slate-950">¿Qué quieres hacer?</h2>
          </div>
          <Settings2 size={18} className="text-slate-300" />
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          {tasks.map((task) => (
            <button key={task.title} type="button" onClick={task.onClick ?? onOpenOperations} className="group rounded-2xl border border-slate-200 bg-white p-5 text-left transition-all hover:border-emerald-300 hover:shadow-md">
              <task.icon size={20} className="text-emerald-700" />
              <h3 className="mt-4 font-black text-slate-900">{task.title}</h3>
              <p className="mt-1 text-xs leading-5 text-slate-500">{task.description}</p>
              <span className="mt-4 flex items-center gap-1.5 text-xs font-black text-emerald-700">Abrir <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" /></span>
            </button>
          ))}
        </div>
      </div>

    </div>
  </div>
);
